import { css } from '@emotion/react';
import {
  useCallback, useMemo,
} from 'react';
import { LottieAnimationComponent } from '~/_shared/baseComponents/lottieAnimation/lottieAnimation.component';
import { LottieAnimationTypes } from '~/_shared/baseComponents/lottieAnimation/lottieAnimation.types';
import {
  type SliderConstraint, SliderConstraintType,
} from '~/_shared/components/slider/slider.component';
import { SliderWithValueLabelsComponent } from '~/_shared/components/slider/sliderWithValueLabels/sliderWithValueLabels.component';
import { clamp } from '~/_shared/utils/number/number.helpers';
import {
  isNullsy, notNullsy,
} from '~/_shared/utils/typeGuards';
import { ConstraintSliderInput } from '~/sidebar/sidebarApps/mapTools/boundary/loadBoundaryPane/createCustomBoundaryPane/createBoundaryGroupFromAIModal/modalSteps/constraintSliderInput.component';

const ROUND_SLIDER_VALUES_THRESHOLD = 100;

const constraintsSliderContainerStyle = css({
  flex: 1,
  margin: '0 20px',
});

const constraintsSliderInputsContainerStyle = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flex: 1,
});

const animationWrapperStyle = css({
  alignSelf: 'flex-start',
  marginTop: -5,
});

const contentStyle = css({
  marginTop: 15,
  display: 'flex',
  alignItems: 'center',
});

type ConstraintSliderProps = {
  animationType?: 'success' | 'error';
  highestMin?: number | null;
  lowestMax?: number | null;
  max: number;
  maxValue?: number;
  min: number;
  minValue?: number;
  ariaLabel: string;
  onChange: (minConstraint: number, maxConstraint: number) => void;
};

export const ConstraintSlider = (props: ConstraintSliderProps) => {
  const {
    animationType,
    highestMin,
    lowestMax,
    ariaLabel,
    onChange,
  } = props;

  const sliderRange = props.max - props.min;
  const roundValues = sliderRange > ROUND_SLIDER_VALUES_THRESHOLD;

  const min = roundValues ? Math.floor(props.min) : props.min;
  const max = roundValues ? Math.ceil(props.max) : props.max;

  const clampedMinValue = isNullsy(props.minValue) ? min : clamp(props.minValue, { min, max });
  const clampedMaxValue = isNullsy(props.maxValue) ? max : clamp(props.maxValue, { min, max });

  const handleChange = useCallback((change: {
    minConstraint?: number;
    maxConstraint?: number;
  }) => {
    // only propagate when something changes
    if (change.minConstraint === clampedMinValue && change.maxConstraint === clampedMaxValue) {
      return;
    }

    let newMin = change.minConstraint ?? clampedMinValue;
    if (roundValues && notNullsy(change.minConstraint)) {
      newMin = Math.max(Math.floor(change.minConstraint), min);
    }

    let newMax = change.maxConstraint ?? clampedMaxValue;
    if (roundValues && notNullsy(change.maxConstraint)) {
      newMax = Math.min(Math.ceil(change.maxConstraint), max);
    }

    if (newMin === clampedMinValue && newMax === clampedMaxValue) {
      return;
    }

    onChange(newMin, newMax);
  }, [clampedMaxValue, clampedMinValue, max, min, onChange, roundValues]);

  const constraints = useMemo(() => {
    const result = [] as SliderConstraint[];

    if (highestMin) {
      result.push(({
        type: SliderConstraintType.LessThan,
        value: highestMin,
      }));
    }

    if (lowestMax) {
      result.push(({
        type: SliderConstraintType.GreaterThan,
        value: lowestMax,
      }));
    }

    if (result.length) {
      return result;
    }
    return undefined;
  }, [highestMin, lowestMax]);

  return (
    <div css={contentStyle}>
      <div css={constraintsSliderContainerStyle}>
        <SliderWithValueLabelsComponent
          noLabel
          min={min}
          max={max}
          value={[clampedMinValue, clampedMaxValue]}
          onChange={([minConstraint, maxConstraint]) => handleChange({ minConstraint, maxConstraint })}
          constraints={constraints}
          aria-label={`Constraint slider for ${ariaLabel}`}
        />

        <div css={constraintsSliderInputsContainerStyle}>
          <ConstraintSliderInput
            max={highestMin ?? undefined}
            min={min}
            value={clampedMinValue}
            onChange={newValue => handleChange({
              minConstraint: newValue > clampedMaxValue ? clampedMaxValue : newValue,
              maxConstraint: newValue > clampedMaxValue ? newValue : clampedMaxValue,
            })}
            aria-label={`Minimum value constraint for ${ariaLabel}`}
          />
          <ConstraintSliderInput
            value={clampedMaxValue}
            min={lowestMax ?? undefined}
            max={max}
            onChange={newValue => handleChange({
              minConstraint: newValue < clampedMinValue ? newValue : clampedMinValue,
              maxConstraint: newValue < clampedMinValue ? clampedMinValue : newValue,
            })}
            aria-label={`Maximum value constraint for ${ariaLabel}`}
          />
        </div>
      </div>

      {animationType !== undefined && (
        <div css={animationWrapperStyle}>
          {animationType === 'success' && (
            <LottieAnimationComponent
              size={24}
              type={LottieAnimationTypes.Success}
            />
          )}

          {animationType === 'error' && (
            <LottieAnimationComponent
              size={24}
              type={LottieAnimationTypes.Failed}
            />
          )}
        </div>
      )}
    </div>
  );
};
